import { Divider, Grid, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { ReactComponent as MicrIcon } from 'src/common/media/micr.svg';
import { AppState, store } from 'src/common/redux/configure-store';
import { MICRActions } from 'src/common/redux/reducers/micr-reducer';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import {
  CheckSide,
  MicrEndorsementTitle,
  MicrInsertCheckBack,
  MicrInsertCheckFront,
  ProcessingIndication,
} from 'src/devices/micr-utilities';
import { TenderingModeHomePage } from 'src/home-page/components/home-page-tendering';
import { themeColor } from 'src/mui-theme';
import { styles } from 'src/tendering/components/process-tender/tender-processing-view-styles';
import TenderAmountDue from 'src/tendering/components/tender-amount-due';
import TenderGroupTitle from 'src/tendering/components/tender-group-title';
import {
  CenterMessagingEndorsementProps,
  PrintEndorsementViewProps,
} from './check-processing-types';
import { styles as micrStyles } from './micr-processing-view-styles';

export const showPrintEndorsementView = (props: PrintEndorsementViewProps): void => {
  setHomePage(<PrintEndorsementView {...props} />, undefined, { isReplaceablePage: true });
};

export const closePrintEndorsementView = (): void => {
  store.dispatch(MICRActions.resetMICRData());
  restorePage(TenderingModeHomePage);
};

const PrintEndorsementView: React.FC<PrintEndorsementViewProps> = (
  props: PrintEndorsementViewProps,
): JSX.Element => {
  const classes = styles();
  const micrClasses = micrStyles();
  const trainingModeClasses = trainingModeStyles();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  const amountDue = useSelector((state: AppState) => state.itemList.amountDue);
  const [displayText, setDisplayText] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [checkSide, setCheckSide] = useState<CheckSide>(props.initialCheckSide);

  const title = store.getState().tenderMenu.selectedTenderGroup?.name || '';

  useEffect(() => {
    if (processing) {
      setDisplayText(ProcessingIndication);
    } else if (checkSide === CheckSide.Front) {
      setDisplayText(MicrInsertCheckFront);
    } else if (checkSide === CheckSide.Back) {
      setDisplayText(MicrInsertCheckBack);
    }
  }, [checkSide, processing, setDisplayText]);

  const onPrinterEndorse = () => {
    setProcessing(true);
    props.onEndorse().catch(err => {
      if (err.code === 'EndorseOtherSide') {
        setCheckSide(CheckSide.Back);
      }
      setProcessing(false);
    });
  };

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='space-between'
      className={`fullHeight flexFull ${classes.viewContainer} ${
        trainingMode ? trainingModeClasses.trainingModeBackground : ''
      }`}
    >
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        className={classes.viewHeader}
      >
        <Grid item lg={9}>
          <TenderGroupTitle title={title} />
        </Grid>
        <Grid item>
          <TenderAmountDue amountDue={amountDue} align='right' />
        </Grid>
      </Grid>

      <Grid item container direction='row' className={micrClasses.pageContent}>
        <CenterMessagingColumn
          displayText={displayText}
          processing={processing}
          onCancel={props.onCancel}
          onManualEndorse={props.onManualEndorse}
          onPrinterEndorse={onPrinterEndorse}
        />
      </Grid>

      {trainingMode && (
        <TrainingModeWatermark
          dataTestId='action-window-training-mode-watermark'
          supplementalStyles={trainingModeClasses.trainingWaterMark}
        />
      )}
    </Grid>
  );
};

export default PrintEndorsementView;

const CenterMessagingColumn: React.FC<CenterMessagingEndorsementProps> = ({
  displayText,
  processing,
  onPrinterEndorse,
  onCancel,
  onManualEndorse,
}) => {
  const classes = styles();
  const micrClasses = micrStyles();
  const { t } = useTranslation();

  return (
    <Grid container md={7} direction='column' className={micrClasses.centerPageContent}>
      <Grid
        item
        container
        direction='column'
        data-testid='payment-info'
        className={micrClasses.checkScreenMessageGridContainer}
      >
        <Grid item data-testid='payment-title' className={micrClasses.checkScreenMessageTitleGrid}>
          <Typography variant='h2'>{t(MicrEndorsementTitle)}</Typography>
        </Grid>
        <Grid item className={micrClasses.checkScreenDividerGrid}>
          <div data-testid='payment-title-bar' className={`${micrClasses.paymentTitleBar}`}></div>
        </Grid>
        <Grid
          item
          data-testid='endorsement-message'
          className={micrClasses.checkScreenMessageBodyGrid}
        >
          <Typography variant='body1'>{t(displayText ?? '')}</Typography>
        </Grid>
        {!processing && (
          <>
            <Grid item>
              <MicrIcon
                width={'2.342606149341142vw'}
                height={'4.166666666666667vh'}
                fill={themeColor.secondaryMain}
              />
            </Grid>
            <Grid item container direction='row' className={micrClasses.dividerContainer}>
              <Grid item className={micrClasses.divider}>
                <Divider />
              </Grid>
              <Grid item className={micrClasses.dividerMiddle}>
                <Typography variant='body1'>{t('or')}</Typography>
              </Grid>
              <Grid item className={micrClasses.divider}>
                <Divider />
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant='body1'>{t('MicrEndorseManually')}</Typography>
            </Grid>
          </>
        )}
      </Grid>
      <Grid container className={micrClasses.checkScreenBottomButtons}>
        <Grid item container direction='row' className={classes.exitButtonContainer}>
          <BaseButton
            color='inherit'
            buttonType='white'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={onCancel}
            data-testid='check-processing-cancel'
          >
            <Typography variant='button'>{t('Cancel')}</Typography>
          </BaseButton>
          <BaseButton
            buttonType='white'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={onManualEndorse}
            data-testid='check-processing-manual'
          >
            <Typography variant='button'>{t('MicrManualButton')}</Typography>
          </BaseButton>
          <BaseButton
            color='secondary'
            className={`${micrClasses.bottomButton}`}
            onClick={onPrinterEndorse}
            data-testid='check-endorsement-button'
          >
            <Typography variant='button'>{t('MicrEndorsementButton')}</Typography>
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
